import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { getRequireReviewTranscriptsApi } from "../../../api/Reads";
import { setGenericModalId } from "../../../Redux/features/modalsSlice";
import { ImSpinner3 } from "react-icons/im";
import { Table } from "../../../components";

const ITEMS_PER_PAGE = 15;

const ManagementTable = ({ ViewModal, requestType }) => {
  const [transcriptDetails, setTranscriptDetails] = useState();
  const [currentPage, setCurrentPage] = useState(0); // 0-indexed
  const [records, setRecords] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();

  // Track whether a requestType change just happened so we can skip the
  // redundant page-change fetch that fires when we reset currentPage to 0.
  const tabChangedRef = useRef(false);

  const fetchPage = useCallback(async (type, page) => {
    setIsLoading(true);
    try {
      const response = await getRequireReviewTranscriptsApi(type, page, ITEMS_PER_PAGE);
      if (response.success) {
        const data =
          response.data?.records ||
          response.data?.items ||
          (Array.isArray(response.data) ? response.data : []);

        // Use the real total count — NOT the current page's item count —
        // so that totalPages is always computed from the full dataset size.
        const totalCount =
          response.data?.totalRecords ||
          response.data?.totalCount ||
          data.length;

        const pages =
          typeof response.data?.totalPages === 'number'
            ? response.data.totalPages + 1
            : (ITEMS_PER_PAGE > 0 ? Math.ceil(totalCount / ITEMS_PER_PAGE) : 1);

        setRecords(Array.isArray(data) ? data : []);
        setTotalPages(pages);
        setTotalItems(totalCount);
      } else {
        toast.error(response.message || "Oops, Something went wrong");
        setRecords([]);
        setTotalPages(0);
        setTotalItems(0);
      }
    } catch (err) {
      toast.error("Oops, Something went wrong");
      setRecords([]);
      setTotalPages(0);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // When the active tab (requestType) changes: reset to page 0 and fetch.
  // Mark tabChangedRef so the currentPage effect below knows to skip its fetch
  // for the reset-to-0 render.
  useEffect(() => {
    tabChangedRef.current = true;
    setCurrentPage(0);
    fetchPage(requestType, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestType]);

  // When the user navigates to a different page: fetch that page.
  // Skip if triggered by a tab change (tabChangedRef), since the tab-change
  // effect already issued the fetch.
  useEffect(() => {
    if (tabChangedRef.current) {
      tabChangedRef.current = false;
      return;
    }
    fetchPage(requestType, currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handlePreviewButtonClick = (orderNo) => {
    const temp = records.find((item) => item.requestOrderNumber === orderNo);
    if (!temp) return;

    const { transcriptDocument } = temp;

    const attachingDocumentUrlArray = JSON.parse(
      transcriptDocument?.[0]?.attachingDocumentUrl || "[]"
    );
    const statementOfResultURL =
      transcriptDocument?.[0]?.clearanceORpaymentRecieptsUrl;

    setTranscriptDetails({
      requestOrderNumber: temp?.requestOrderNumber,
      trancriptUseCaseType: temp?.trancriptUseCaseType,
      destination: temp?.address1,
      secondDestination: temp?.address2,
      CityName: temp?.city?.name,
      CountryName: temp?.country?.name,
      regNumber: temp?.regNumber,
      departmentName: temp?.student?.admission?.department?.department_Name,
      fullName: temp?.student?.user?.fullName,
      email: temp?.student?.user?.email,
      phoneNumber: temp?.student?.user?.phoneNumber,
      dateOfBirth: temp?.dateOfBirth,
      educationType: temp?.educationType,
      attachingDocumentUrlArray,
      referenceNumber: temp?.referenceNumber,
      resultType: temp?.resultType,
      destinationContactNumber: temp?.destinationContactNumber,
      organisation: temp?.organisation,
      statementOfResultURL,
    });

    dispatch(setGenericModalId("UnprocessedTranscriptView"));
  };

  const columns = [
    { value: "Serial No." },
    { value: "Order No." },
    { value: "Full Name" },
    { value: "Request Date" },
    { value: "Matric No." },
    { value: "Shipping Address" },
    { value: "Destination Email" },
    requestType === 3 && { value: "Education Type" },
    { value: "Status" },
    { value: "Actions" },
  ].filter(Boolean);

  const getStatusDisplay = (status) => {
    switch (status) {
      case 6:
        return {
          text: "Ready to be processed",
          className:
            "inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200",
        };
      case 9:
        return {
          text: "Pending bursary approval",
          className:
            "inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200",
        };
      default:
        return {
          text: "Unknown",
          className:
            "inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-50 text-slate-700 border border-slate-200",
        };
    }
  };

  const tableData = records.map((item, index) => {
    const statusInfo = getStatusDisplay(item.status);
    const serialNumber = currentPage * ITEMS_PER_PAGE + index + 1;

    return {
      "Serial No.": serialNumber,
      "Order No.": item.requestOrderNumber ?? "N/A",
      "Full Name": item.student?.user?.fullName ?? "N/A",
      "Request Date": item.createdAt?.split("T")[0] ?? "N/A",
      "Matric No.": item.regNumber ?? "N/A",
      "Shipping Address": item.address1 ?? "N/A",
      "Destination Email": item.destinationEmail ?? "N/A",
      ...(requestType === 3 && { "Education Type": item.educationType }),
      Status: (
        <span className={statusInfo.className}>{statusInfo.text}</span>
      ),
      Actions: (
        <button
          onClick={() => handlePreviewButtonClick(item.requestOrderNumber)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm font-medium transition-colors duration-200"
        >
          Preview
        </button>
      ),
    };
  });

  return (
    <>
      <div className="grid w-full h-full">
        {isLoading ? (
          <ImSpinner3 className="animate-spin place-self-center" size={32} />
        ) : (
          <Table
            columns={columns}
            data={tableData}
            pageCount={totalPages}
            itemCount={totalItems}
            ChangePage={handlePageChange}
            currentPage={currentPage + 1}
          />
        )}
      </div>
      <ViewModal transcriptDetails={transcriptDetails} />
    </>
  );
};

export default ManagementTable;
